import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import ScrollTopButton from '@/components/feature/ScrollTopButton';
import PageHeader from '@/components/feature/PageHeader';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import FaqAccordion from '@/components/feature/FaqAccordion';
import SectionHeading from '@/components/feature/SectionHeading';
import ContactForm from './components/ContactForm';
import { faqs, site, stats, testimonials } from '@/mocks/site';
import { useSeo } from '@/hooks/useSeo';

const positioning = [
  {
    icon: 'ri-layout-masonry-line',
    title: '不做标题党',
    desc: '标题只写事件本身，不用「震惊」「炸裂」这类词。点进来看到什么，标题就说什么。',
  },
  {
    icon: 'ri-time-line',
    title: '只做时间线',
    desc: '一件事只有一个页面，所有进展按时间追加，不拆成十几条重复的碎片内容。',
  },
  {
    icon: 'ri-scales-3-line',
    title: '标清状态',
    desc: '每条素材都会标注「已归档 / 存疑 / 待核实」，把判断权留给读者。',
  },
];

const principles = [
  '只整理公开网络上可追溯的内容，不发布任何未公开的私密信息。',
  '人物一律以身份代称呈现，避免把未经证实的信息指向具体个人。',
  '引用录音、截图时保留原始时长与发布账号，注明可能的剪辑点。',
  '事件出现反转时以补充条目的方式更新，不删除、不悄悄改写旧内容。',
  '不接付费删帖，也不接受任何一方提供的「独家通稿式」内容。',
];

const steps = [
  { step: '01', title: '锁定源头', desc: '找到最早的公开爆料帖，记录发布时间与发布账号。' },
  { step: '02', title: '收集素材', desc: '整理各方回应、录音、截图与媒体报道，逐条登记出处。' },
  { step: '03', title: '交叉比对', desc: '把互相矛盾的时间点单独标出，能核实的写在时间线上。' },
  { step: '04', title: '持续更新', desc: '后续进展以补充条目追加，并同步更新档案状态标签。' },
];

export default function About() {
  useSeo({
    title: '关于我们 - 瓜田档案局的内容原则与整理流程',
    description:
      '了解瓜田档案局的定位、内容原则与整理流程：我们如何整理网曝黑料合集帖、如何标注录音证据与聊天记录的真伪状态，以及如何联系我们投稿或纠错。',
    keywords: '瓜田档案局,关于我们,黑料合集站,内容原则,素材投稿',
    path: '/about',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于瓜田档案局',
        description: '瓜田档案局的品牌介绍、定位、内容原则与联系方式。',
        url: 'https://ddysyjy.cn/about',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: site.name,
        email: site.email,
        foundingDate: site.since,
        description: site.intro,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
      },
    ],
  });

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <PageHeader
          eyebrow="关于我们"
          title="把一件瓜，整理成一份能对得上的档案"
          desc={`${site.name}从 ${site.since} 年开始做事件整理。我们不做审判者，只做记录者——把公开的爆料、回应与反转按时间排好，标清来源，剩下的交给你判断。`}
        />

        <div className="mx-auto max-w-content px-4 md:px-6 py-10 md:py-14">
          <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

          <section className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div className="overflow-hidden rounded-lg bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20editorial%20flatlay%20of%20paper%20archive%20folders%2C%20printed%20timeline%20sheets%20and%20a%20magnifying%20glass%20on%20a%20cream%20linen%20desk%2C%20soft%20amber%20daylight%2C%20minimal%20composition%2C%20muted%20terracotta%20accents%2C%20high%20detail&width=800&height=600&seq=guatian-about-01&orientation=landscape"
                alt="瓜田档案局的档案整理工作台 - 时间线纸张与放大镜"
                title="瓜田档案局 内容整理流程"
                className="w-full h-72 object-cover object-top md:h-96"
              />
            </div>
            <div>
              <span className="mb-2 flex items-center gap-2 text-xs font-medium tracking-[0.2em] text-primary-600">
                <i className="ri-quill-pen-line"></i>
                品牌介绍
              </span>
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                为什么我们只做「整理」
              </h2>
              <div className="mt-5 space-y-4 text-sm leading-loose text-foreground-700 md:text-base">
                <p>
                  一开始，我们也是一群在评论区里被碎片信息绕晕的普通吃瓜人。同一个事件，十几个账号给出十几种时间顺序，谁也说不清到底哪一句是真的。
                </p>
                <p>
                  于是我们决定换一种做法：不抢首发，不下定论，只把已经公开的素材按时间排好，把矛盾的地方如实标出来。这份「不站队」的克制，后来成了 {site.name} 最被读者认可的地方。
                </p>
                <p>
                  到现在，我们累计归档了 392 起事件、1,860 份录音与截图素材，也总结出一套可复用的整理流程。
                </p>
              </div>
              <dl className="mt-8 grid grid-cols-2 gap-6 border-t border-background-200 pt-6">
                {stats.slice(0, 2).map((item) => (
                  <div key={item.label}>
                    <dd className="font-heading text-2xl font-bold text-primary-600 md:text-3xl">
                      {item.value}
                      <span className="ml-1 text-sm font-normal text-foreground-500">{item.unit}</span>
                    </dd>
                    <dt className="mt-1 text-xs text-foreground-500">{item.label}</dt>
                  </div>
                ))}
              </dl>
            </div>
          </section>

          <section className="mt-16 md:mt-20">
            <SectionHeading eyebrow="网站定位" title="三件事，我们坚持不做" />
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
              {positioning.map((item) => (
                <article key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                  <span className="flex h-12 w-12 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                    <i className={`${item.icon} text-2xl`}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </article>
              ))}
            </div>
          </section>
        </div>

        <section className="bg-background-100">
          <div className="mx-auto max-w-content px-4 md:px-6 py-14 md:py-20">
            <SectionHeading
              eyebrow="内容原则"
              title="我们给自己定的五条规矩"
              desc="这些规矩决定了哪些素材会被收录，也决定了档案更新时会怎么做。"
            />
            <ol className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {principles.map((text, index) => (
                <li key={text} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-500 font-heading text-sm font-bold text-background-50">
                    {index + 1}
                  </span>
                  <p className="text-sm leading-relaxed text-foreground-700">{text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <div className="mx-auto max-w-content px-4 md:px-6 py-14 md:py-20">
          <section>
            <SectionHeading eyebrow="整理流程" title="一份档案是这样成型的" />
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {steps.map((item) => (
                <div key={item.step} className="rounded-lg border border-background-200 bg-background-50 p-6">
                  <span className="font-heading text-3xl font-black text-primary-200">{item.step}</span>
                  <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-16 border-t border-background-200 pt-14 md:mt-20">
            <SectionHeading eyebrow="用户口碑" title="读者们怎么说" />
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
              {testimonials.map((item) => (
                <blockquote key={item.name} className="rounded-lg border border-background-200 bg-background-50 p-6">
                  <i className="ri-double-quotes-l text-2xl text-accent-500"></i>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-700">{item.content}</p>
                  <footer className="mt-5 border-t border-background-200 pt-4 text-xs">
                    <strong className="block text-foreground-900">{item.name}</strong>
                    <span className="text-foreground-500">{item.role}</span>
                  </footer>
                </blockquote>
              ))}
            </div>
          </section>

          <section className="mt-16 border-t border-background-200 pt-14 md:mt-20">
            <SectionHeading eyebrow="常见问题" title="关于内容，你可能还想知道" />
            <FaqAccordion items={faqs} />
          </section>

          <section className="mt-16 border-t border-background-200 pt-14 md:mt-20">
            <SectionHeading
              eyebrow="联系我们"
              title="投稿、纠错或补充进展"
              desc="如果你手上有更完整的公开素材，或发现档案里的时间点有误，欢迎告诉我们。我们会逐条核对后更新。"
            />
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
              <div className="lg:col-span-1">
                <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                  <h3 className="font-heading text-base font-bold text-foreground-950">直接联系</h3>
                  <ul className="mt-5 space-y-4 text-sm">
                    <li className="flex items-start gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                        <i className="ri-mail-line"></i>
                      </span>
                      <span>
                        <span className="block text-xs text-foreground-500">联系邮箱</span>
                        <a href={`mailto:${site.email}`} className="text-foreground-800 hover:text-primary-600">
                          {site.email}
                        </a>
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                        <i className="ri-time-line"></i>
                      </span>
                      <span>
                        <span className="block text-xs text-foreground-500">核对周期</span>
                        <span className="text-foreground-800">收到后 3 个工作日内逐条核对</span>
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                        <i className="ri-shield-keyhole-line"></i>
                      </span>
                      <span>
                        <span className="block text-xs text-foreground-500">隐私说明</span>
                        <span className="text-foreground-800">邮箱仅用于回复，不做他用</span>
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="lg:col-span-2">
                <ContactForm />
              </div>
            </div>
          </section>
        </div>
      </main>
      <SiteFooter />
      <ScrollTopButton />
    </div>
  );
}